import { Injectable } from '@angular/core';
import { generateClient } from 'aws-amplify/api';
import * as queries from '@graphql/queries';
import * as mutations from '@graphql/mutations';
import {
    CreateUserInput,
    ModelSortDirection,
    ModelUserFilterInput,
    UpdateUserInput,
    User
} from '@app/core/services/api/API.service';
import { BehaviorSubject, Subscription, firstValueFrom, take } from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class UserService {
    private amplifyClient = generateClient();
    private _userByIdCache = new Map<string, User>();
    private _userByIdInFlight = new Map<string, Promise<User | null>>();

    constructor() {}

    clearSessionCache(): void {
        this._userByIdCache.clear();
        this._userByIdInFlight.clear();
    }

    async listAllUsers(pageLimit: number = 10): Promise<User[]> {
        const users: User[] = [];
        let nextToken: string | null = null;

        try {
            do {
                const response = (
                    await this.amplifyClient.graphql({
                        query: queries.listUsers,
                        variables: {
                            limit: pageLimit,
                            nextToken
                        }
                    })
                ).data.listUsers;
                users.push(...((response?.items ?? []) as User[]));
                nextToken = response?.nextToken ?? null;
            } while (nextToken !== null);
            return users;
        } catch (error) {
            console.error('Error listing users:', error);
            return [];
        }
    }

    async findUserById(id: string): Promise<User> {
        const cached = this._userByIdCache.get(id);
        if (cached) {
            return cached;
        }

        const inFlight = this._userByIdInFlight.get(id);
        if (inFlight) {
            return inFlight;
        }

        const promise = this.fetchUserById(id);
        this._userByIdInFlight.set(id, promise);

        try {
            const user = await promise;
            if (user) {
                this._userByIdCache.set(id, user);
            }
            return user;
        } finally {
            this._userByIdInFlight.delete(id);
        }
    }

    private async fetchUserById(id: string): Promise<User> {
        try {
            const user: User = (
                await this.amplifyClient.graphql({
                    query: queries.getUser,
                    variables: {
                        id
                    }
                })
            ).data.getUser;
            if (user) return user;
        } catch (error) {
            console.error('Error finding user:', error);
            return null;
        }
    }

    async getUserByRol(
        role: string,
        filter: ModelUserFilterInput = null
    ): Promise<User[]> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: queries.GetUserByRol,
                    variables: {
                        role,
                        filter
                    }
                })
            ).data.GetUserByRol.items;
            if (data) return data as User[];
        } catch (error) {
            console.error('Error finding User:', error);
            return null;
        }
    }

    async listAllUsersByRol(
        role: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC
    ): Promise<User[]> {
        const users: User[] = [];
        let nextToken: string | null = null;

        do {
            const response = (
                await this.amplifyClient.graphql({
                    query: queries.GetUserByRol,
                    variables: {
                        role,
                        limit: 20,
                        nextToken,
                        sortDirection
                    }
                })
            ).data.GetUserByRol;
            users.push(...((response?.items ?? []) as User[]));
            nextToken = response?.nextToken ?? null;
        } while (nextToken !== null);

        return users;
    }

    async listAllUsersByCompanyId(
        companyId: string,
        sortDirection: ModelSortDirection = ModelSortDirection.DESC
    ): Promise<User[]> {
        const users: User[] = [];
        let nextToken: string | null = null;

        do {
            const result = await this.amplifyClient.graphql({
                query: queries.GetUserByCompanyId,
                variables: {
                    companyId,
                    limit: 20,
                    nextToken,
                    sortDirection
                }
            });
            const response = result.data.GetUserByCompanyId;
            users.push(...((response?.items ?? []) as User[]));
            nextToken = response?.nextToken ?? null;
        } while (nextToken !== null);

        return users;
    }

    async getUserEmail(email: string): Promise<User[]> {
        try {
            const data = ((await this.amplifyClient.graphql({
                query: queries.GetUserByEmail,
                variables: {
                    email
                },
                authMode: 'apiKey'
            })) as any).data.GetUserByEmail.items;
            if (data) return data as User[];
        } catch (error) {
            console.error('Error finding User:', error);
            return null;
        }
    }

    async getUserByCompanyId(
        companyId: string,
        filter: ModelUserFilterInput = null,
        limit: number = 10000
    ): Promise<User[]> {
        try {
            const result = await this.amplifyClient.graphql({
                query: queries.GetUserByCompanyId,
                variables: { companyId, filter, limit }
            });
            const users: User[] = result.data.GetUserByCompanyId.items;
            return users;
        } catch (error) {
            console.error('Error fetching users by company ID:', error);
            return null;
        }
    }

    async updateUser(input: UpdateUserInput): Promise<User> {
        try {
            const user: User = (
                await this.amplifyClient.graphql({
                    query: mutations.updateUser,
                    variables: {
                        input
                    }
                })
            ).data.updateUser;
            if (user) return user;
        } catch (error) {
            console.error('Error updating user:', error);
            return null;
        }
    }

    async updateUserStatus({ id, status }: UpdateUserInput): Promise<User> {
        try {
            const user: User = (
                await this.amplifyClient.graphql({
                    query: mutations.updateUser,
                    variables: {
                        input: {
                            id,
                            status
                        }
                    }
                })
            ).data.updateUser;
            if (user) return user;
        } catch (error) {
            console.error('Error updating user:', error);
            return null;
        }
    }
    async updateUserIsManagement({
        id,
        isManagement
    }: UpdateUserInput): Promise<User> {
        try {
            const user: User = (
                await this.amplifyClient.graphql({
                    query: mutations.updateUser,
                    variables: {
                        input: {
                            id,
                            isManagement
                        }
                    }
                })
            ).data.updateUser;
            if (user) return user;
        } catch (error) {
            console.error('Error updating user:', error);
            return null;
        }
    }
    async updateUserIsAdministration({
        id,
        isAdministration
    }: UpdateUserInput): Promise<User> {
        try {
            const user: User = (
                await this.amplifyClient.graphql({
                    query: mutations.updateUser,
                    variables: {
                        input: {
                            id,
                            isAdministration
                        }
                    }
                })
            ).data.updateUser;
            if (user) return user;
        } catch (error) {
            console.error('Error updating user:', error);
            return null;
        }
    }

    async updateUserHasNotifications({
        id,
        hasNotifications
    }: UpdateUserInput): Promise<User> {
        try {
            const user: User = (
                await this.amplifyClient.graphql({
                    query: mutations.updateUser,
                    variables: {
                        input: {
                            id,
                            hasNotifications
                        }
                    }
                })
            ).data.updateUser;
            if (user) return user;
        } catch (error) {
            console.error('Error updating user:', error);
            return null;
        }
    }

    async updateUserIsLogistic({
        id,
        isLogistic
    }: UpdateUserInput): Promise<User> {
        try {
            const user: User = (
                await this.amplifyClient.graphql({
                    query: mutations.updateUser,
                    variables: {
                        input: {
                            id,
                            isLogistic
                        }
                    }
                })
            ).data.updateUser;
            if (user) return user;
        } catch (error) {
            console.error('Error updating user:', error);
            return null;
        }
    }

    async createUser(input: CreateUserInput): Promise<User> {
        try {
            const data = (
                await this.amplifyClient.graphql({
                    query: mutations.createUser,
                    variables: {
                        input
                    }
                })
            ).data.createUser;
            if (data) return data as User;
        } catch (error) {
            console.error('Error creating user:', error);
            return null;
        }
    }
}
